/*
 * -----------------------------------------------------------------------
 *  <copyright file="step3-preview.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useEffect, forwardRef, useImperativeHandle } from 'react';
import { useMutation } from '@apollo/client/react';
import { PanelPreview } from '@components/central-monitoring/panels/add-panel/panel-preview';
import { useBoolean, useLazyQueryConvertToJson, useLocalState, useTranslation } from 'hooks';
import { EXECUTE_PANEL, GET_PANELS, SAVE_PANEL } from 'queries/central-monitoring';
import { PanelDisplayUnits } from 'types/graphql-global-types';
import Constants from 'utils/constants';
import NotificationService from 'utils/notification';
import PanelDisplay from 'views/components/central-monitoring/panels/panel-display';
import Loading from 'views/components/core/loading';
import 'views/components/central-monitoring/panels/add-panel/styles.scss';
import { upsertPanel, upsertPanelVariables } from '@queries/generated/central-monitoring.generated';

const Step3Preview = forwardRef(
    (
        {
            panelState,
            getPanelFromState,
            onClose,
            studyId,
            user,
            currentPanel,
            showReasonForChangeDialog,
            setLoadingState,
            show,
            dashboardType,
            siteId,
        }: any,
        ref,
    ) => {
        const [loadPanel, { convertedData: panelData, loading: panelLoading }] = useLazyQueryConvertToJson(
            EXECUTE_PANEL,
            (convertedData: any) => {
                // eslint-disable-next-line no-console
                console.log(convertedData, 'Panel Preview Data');
            },
        );

        const { state: localState } = useLocalState();
        const [savePanel] = useMutation<upsertPanel, upsertPanelVariables>(SAVE_PANEL);
        const { t } = useTranslation();
        const disabledSave = useBoolean(false);

        const panel = getPanelFromState();
        panel.studyId = localState.selectedStudy.id;
        const selectedDashboardType = dashboardType;

        const isLatestCM = localState?.selectedStudy?.useLatestCentralMonitoring;

        useEffect(() => {
            setLoadingState(panelLoading);
        }, [panelLoading]);

        useEffect(() => {
            if (panel) {
                panel.graph.displayUnits =
                    panel.graph.displayUnits === '' ? PanelDisplayUnits.PERCENT : panel.graph.displayUnits;
                panel.graph.dataInclusionUnit = Number(panel.graph.dataInclusionUnit)
                    ? Number(panel.graph.dataInclusionUnit)
                    : 0;
            }
        });

        useEffect(() => {
            if (show && !isLatestCM)
                loadPanel({
                    variables: {
                        panel,
                    },
                });
        }, [show]);

        const onSave = (value?: string) => {
            if (currentPanel && !value) showReasonForChangeDialog.setTrue();
            else {
                setLoadingState(true);
                disabledSave.setTrue();
                savePanel({
                    variables: {
                        panel,
                        reason: currentPanel ? value! : '',
                    },
                    refetchQueries: [
                        {
                            query: GET_PANELS,
                            variables: {
                                organisationId: user.organisationId,
                                studyId: studyId ?? Constants.ObjectId.Empty,
                            },
                        },
                    ],
                })
                    .then((result: any) => {
                        setLoadingState(false);
                        disabledSave.setFalse();
                        if (!result?.data?.upsertPanel) NotificationService.showNotification(t('errorMessage'));
                        else {
                            if (currentPanel)
                                NotificationService.showNotification(
                                    t('admin:panelSuccessfulUpdated', {
                                        id: currentPanel?.recordId ?? currentPanel?.id,
                                    }),
                                );
                            else NotificationService.showNotification(t('admin:panelSuccessfulCreate'));
                            onClose();
                        }
                    })
                    .catch((e: any) => {
                        setLoadingState(false);
                        NotificationService.showNotification(t('errorMessage'));
                        disabledSave.setFalse();
                        // eslint-disable-next-line no-console
                        console.warn('Error saving panel: ', e);
                    });
            }
        };

        useImperativeHandle(ref, () => ({
            onSaveStep(value?: string) {
                onSave(value);
            },
        }));

        return (
            <div className="step3" style={!show ? { display: 'none' } : {}}>
                {!isLatestCM && panelLoading && <Loading />}
                {!isLatestCM && !panelLoading && panelData?.panelData?.length > 0 && !panelData?.error && (
                    <PanelDisplay
                        type={panelState.panelType}
                        modelConfig={panelState.modelParams}
                        chartConfig={panelState.graph}
                        panelData={panelData}
                        previewMode
                        siteId={siteId}
                        dashboardType={selectedDashboardType}
                    />
                )}
                {!isLatestCM && panelData?.error && <h1 className="no-data">{panelData.error}</h1>}
                {isLatestCM && show && <PanelPreview studyRef={studyId} panelState={panel} />}
            </div>
        );
    },
);

export default Step3Preview;
